// Global JavaScript for LiftaClub Theme

class LiftaClubTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.initializeComponents();
    this.setupAnimations();
  }

  setupEventListeners() {
    // Mobile menu toggle
    const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    
    if (mobileMenuToggle && mobileMenu) {
      mobileMenuToggle.addEventListener('click', () => {
        mobileMenu.classList.toggle('hidden');
        mobileMenuToggle.setAttribute('aria-expanded', 
          mobileMenu.classList.contains('hidden') ? 'false' : 'true'
        );
      });
    }

    // Smooth scrolling for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });

    // Cart drawer functionality
    this.setupCartDrawer();
    
    // Search functionality
    this.setupSearch();
  }

  initializeComponents() {
    // Initialize lazy loading for images
    this.initLazyLoading();
    
    // Initialize intersection observer for animations
    this.initIntersectionObserver();
    
    // Initialize product variants
    this.initProductVariants();
  }

  setupAnimations() {
    // Add animation classes when elements come into view
    const animatedElements = document.querySelectorAll('[data-animate]');
    
    animatedElements.forEach(element => {
      const animationType = element.dataset.animate;
      element.classList.add('opacity-0');
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0');
            entry.target.classList.add(`animate-${animationType}`);
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      
      observer.observe(element);
    });
  }

  setupCartDrawer() {
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartToggle = document.querySelector('[data-cart-toggle]');
    const cartClose = document.querySelector('[data-cart-close]');
    
    if (cartToggle && cartDrawer) {
      cartToggle.addEventListener('click', () => {
        cartDrawer.classList.remove('hidden');
        document.body.classList.add('overflow-hidden');
      });
    }
    
    if (cartClose && cartDrawer) {
      cartClose.addEventListener('click', () => {
        cartDrawer.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      });
    }
  }

  setupSearch() {
    const searchInput = document.querySelector('[data-search-input]');
    const searchResults = document.querySelector('[data-search-results]');
    
    if (searchInput && searchResults) {
      let searchTimeout;
      
      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();
        
        if (query.length < 2) {
          searchResults.classList.add('hidden');
          return;
        }
        
        searchTimeout = setTimeout(() => {
          this.performSearch(query, searchResults);
        }, 300);
      });
    }
  }

  async performSearch(query, resultsContainer) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await response.json();
      
      if (data.resources && data.resources.results && data.resources.results.products) {
        this.displaySearchResults(data.resources.results.products, resultsContainer);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  displaySearchResults(products, container) {
    if (products.length === 0) {
      container.innerHTML = '<p class="p-4 text-gray-600">No products found</p>';
    } else {
      container.innerHTML = products.map(product => `
        <a href="${product.url}" class="block p-4 hover:bg-gray-50 border-b">
          <div class="flex items-center space-x-3">
            <img src="${product.image}" alt="${product.title}" class="w-12 h-12 object-cover rounded">
            <div>
              <h3 class="font-medium text-gray-900">${product.title}</h3>
              <p class="text-sm text-gray-600">${product.price}</p>
            </div>
          </div>
        </a>
      `).join('');
    }
    
    container.classList.remove('hidden');
  }

  initLazyLoading() {
    const images = document.querySelectorAll('img[data-src]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.remove('opacity-0');
            img.classList.add('opacity-100');
            imageObserver.unobserve(img);
          }
        });
      });
      
      images.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for older browsers
      images.forEach(img => {
        img.src = img.dataset.src;
        img.classList.remove('opacity-0');
        img.classList.add('opacity-100');
      });
    }
  }

  initIntersectionObserver() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);
    
    // Observe elements with animation classes
    document.querySelectorAll('[data-animate-on-scroll]').forEach(el => {
      observer.observe(el);
    });
  }

  initProductVariants() {
    const variantSelectors = document.querySelectorAll('[data-variant-selector]');
    
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const selectedVariant = e.target.value;
        this.updateProductVariant(selectedVariant);
      });
    });
  }

  updateProductVariant(variantId) {
    // Update product form with selected variant
    const productForm = document.querySelector('[data-product-form]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }
    }
    
    // Update product price, availability, etc.
    this.fetchVariantData(variantId);
  }

  async fetchVariantData(variantId) {
    try {
      const response = await fetch(`/products/${window.productHandle}.js`);
      const product = await response.json();
      const variant = product.variants.find(v => v.id == variantId);
      
      if (variant) {
        this.updateVariantDisplay(variant);
      }
    } catch (error) {
      console.error('Error fetching variant data:', error);
    }
  }

  updateVariantDisplay(variant) {
    // Update price
    const priceElement = document.querySelector('[data-product-price]');
    if (priceElement) {
      priceElement.textContent = this.formatMoney(variant.price);
    }
    
    // Update availability
    const addToCartButton = document.querySelector('[data-add-to-cart]');
    if (addToCartButton) {
      if (variant.available) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = 'Add to Cart';
      } else {
        addToCartButton.disabled = true;
        addToCartButton.textContent = 'Sold Out';
      }
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
}

// Utility functions
const utils = {
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  },

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }
};

// Initialize theme when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new LiftaClubTheme();
});

// Export for use in other scripts
window.LiftaClubTheme = LiftaClubTheme;
window.LiftaClubUtils = utils;
